import React, { useEffect, useContext, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import ProductHeader from "../../components/ProductDetails/ProductHeader";
import ImageCarousel from "../../components/ProductDetails/ImageCarousel";
import ProductInfo from "../../components/ProductDetails/ProductInfo";
import AdBanner from "../../components/ProductDetails/AdBanner";
import TransactionMethods from "../../components/ProductDetails/TransactionMethods";
import SellerInfo from "../../components/ProductDetails/SellerInfo";
import SafeTransactionBanner from "../../components/ProductDetails/SafeTransactionBanner";
import ProductContent from "../../components/ProductDetails/ProductContent";
import Footer from "../../components/ProductDetails/Footer";
import { ProductContext } from "../../context/ProductContext";

const ProductDetailsPage: React.FC = () => {
  const { product_id } = useParams<{ product_id: string }>();
  const navigate = useNavigate();
  const context = useContext(ProductContext);
  const [loadError, setLoadError] = useState<string | null>(null);

  if (!context) {
    throw new Error("ProductContext를 찾을 수 없습니다.");
  }

  const { productInfo, productImage, productImages, sellerInfo, isLoading, loadProductData } =
    context;

  useEffect(() => {
    const loadProduct = async () => {
      if (!product_id) {
        console.error("상품 ID가 없습니다");
        setLoadError("상품 ID가 없습니다");
        return;
      }

      console.log("ProductDetailsPage - 상품 ID:", product_id);
      
      try {
        setLoadError(null);
        await loadProductData(product_id);
        console.log("상품 데이터 로드 완료");
      } catch (error) {
        console.error("상품 데이터 로드 오류:", error);
        if (error instanceof Error) {
          setLoadError(`상품 정보를 불러오는데 실패했습니다: ${error.message}`);
        } else {
          setLoadError("상품 정보를 불러오는데 실패했습니다");
        }
      }
    };

    loadProduct();
  }, [product_id, loadProductData]);

  // 디버깅용 로그
  useEffect(() => {
    console.log("ProductDetailsPage 렌더링 상태:", {
      isLoading,
      hasProductInfo: !!productInfo,
      hasSellerInfo: !!sellerInfo,
      loadError
    });
  }, [isLoading, productInfo, sellerInfo, loadError]);

  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>상품 정보를 불러오는 중입니다...</LoadingText>
      </LoadingContainer>
    );
  }

  if (loadError) {
    return (
      <ErrorContainer>
        <ErrorMessage>{loadError}</ErrorMessage>
        <GoBackButton onClick={() => navigate("/")}>
          홈으로 돌아가기
        </GoBackButton>
      </ErrorContainer>
    );
  }

  if (!productInfo) {
    return (
      <ErrorContainer>
        <ErrorMessage>상품을 찾을 수 없습니다.</ErrorMessage>
        <GoBackButton onClick={() => navigate("/")}>
          홈으로 돌아가기
        </GoBackButton>
      </ErrorContainer>
    );
  }

  const handleOrderClick = () => {
    navigate(`/product/${product_id}/order-payment`);
  };

  return (
    <Container>
      <ProductHeader title="중고나라" />
      <Content>
        <ImageCarousel images={productImages && productImages.length ? productImages : [productImage]} alt={productInfo.title} />
        <ProductInfo
          topCategory={productInfo.topCategory}
          bottomCategory={productInfo.bottomCategory}
          title={productInfo.title}
          description={productInfo.description}
          price={productInfo.price}
          status={productInfo.status}
        />
        <AdBanner
          src="https://cdn.builder.io/api/v1/image/assets/TEMP/a747704bf54dd1631773a735b8a9faf330c73a52b28090dda07858a7dfa02fb4?placeholderIfAbsent=true&apiKey=4c6e8411f77f4625a1a5570dd0e6c4a9"
          alt="광고 배너"
        />
        <TransactionMethods onOrderClick={handleOrderClick} />
        {sellerInfo && <SellerInfo seller={sellerInfo} />}
        <SafeTransactionBanner product_id={product_id!} />
        {/* ProductContent 호출 방식 변경 */}
        <ProductContent htmlString={productInfo.content} />
      </Content>
      <Footer
        likeCount={0}
        viewCount={productInfo.views}
        date={productInfo.date}
      />
    </Container>
  );
};

const Container = styled.main`
  max-width: 640px;
  margin: 0 auto;
  background-color: #fff;
  font-family: Inter, sans-serif;
  overflow-x: hidden;
  position: relative;
  padding-top: 64px;
  padding-bottom: 60px;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  transform-origin: top left;
  @media (max-width: 640px) {
    transform: scale(0.8);
    width: 125%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fff;
`;

const LoadingText = styled.p`
  margin-top: 16px;
  color: #4a5568;
  font-size: 16px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #27c360;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 100vh;
  background-color: #fff;
  padding: 0 20px;
  text-align: center;
`;

const ErrorMessage = styled.p`
  color: #e53e3e;
  font-size: 18px;
  margin-bottom: 20px;
`;

const GoBackButton = styled.button`
  background-color: #4299e1;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #3182ce;
  }
`;

export default ProductDetailsPage;
